/**
 * The permission slugs the UI gates on.
 *
 * These mirror the backend catalogue in
 * `app/Domains/Identity/Enums/Permission.php`. Hiding a control here is a
 * courtesy to the user, never a security boundary — the server re-checks every
 * request regardless of what the UI chose to render.
 */
export const PERMISSIONS = {
  tenantView: 'organization.tenant.view',
  tenantUpdate: 'organization.tenant.update',
  tenantSettingsView: 'organization.tenant.settings.view',
  tenantSettingsUpdate: 'organization.tenant.settings.update',

  regionView: 'organization.region.view',
  regionCreate: 'organization.region.create',
  regionUpdate: 'organization.region.update',
  regionArchive: 'organization.region.archive',

  branchView: 'organization.branch.view',
  branchCreate: 'organization.branch.create',
  branchUpdate: 'organization.branch.update',
  branchArchive: 'organization.branch.archive',

  warehouseView: 'organization.warehouse.view',
  warehouseCreate: 'organization.warehouse.create',
  warehouseUpdate: 'organization.warehouse.update',
  warehouseArchive: 'organization.warehouse.archive',
  warehouseBranchesManage: 'organization.warehouse.branches.manage',

  categoryView: 'organization.category.view',
  categoryCreate: 'organization.category.create',
  categoryUpdate: 'organization.category.update',
  categoryArchive: 'organization.category.archive',

  // COM-07 / COM-08 — spending the capacity the organisation bought. None of
  // these can change how much capacity there is; that is a Fidelen decision
  // behind a separate identity.
  purchasingUnitView: 'commercial.purchasing_unit.view',
  purchasingUnitManage: 'commercial.purchasing_unit.manage',
  purchasingUnitActivate: 'commercial.purchasing_unit.activate',
  purchasingUnitDeactivate: 'commercial.purchasing_unit.deactivate',
  // N2 — archiving a unit is its own right: the Client Super Admin's, or a
  // role it is delegated to. Nothing else offers the unit's Archive.
  purchasingUnitArchive: 'commercial.purchasing_unit.archive',

  currencyView: 'organization.currency.view',
  currencyManage: 'organization.currency.manage',
  taxCodeView: 'organization.tax_code.view',
  taxCodeManage: 'organization.tax_code.manage',

  userView: 'identity.user.view',
  userCreate: 'identity.user.create',
  userUpdate: 'identity.user.update',
  userDeactivate: 'identity.user.deactivate',
  userRolesAssign: 'identity.user.roles.assign',
  userScopesAssign: 'identity.user.scopes.assign',
  userPasswordReset: 'identity.user.password.reset',

  roleView: 'identity.role.view',
  roleCreate: 'identity.role.create',
  roleUpdate: 'identity.role.update',
  roleDelete: 'identity.role.delete',
  rolePermissionsManage: 'identity.role.permissions.manage',

  delegationViewOwn: 'identity.delegation.view_own',
  delegationViewAny: 'identity.delegation.view_any',
  delegationCreateOwn: 'identity.delegation.create_own',
  delegationCreateAny: 'identity.delegation.create_any',
  delegationRevokeOwn: 'identity.delegation.revoke_own',
  delegationRevokeAny: 'identity.delegation.revoke_any',

  // ── Epic 2 ─────────────────────────────────────────────────────────────
  supplierView: 'supplier.supplier.view',
  supplierCreate: 'supplier.supplier.create',
  supplierUpdate: 'supplier.supplier.update',
  supplierActivate: 'supplier.supplier.activate',
  supplierSuspend: 'supplier.supplier.suspend',
  supplierArchive: 'supplier.supplier.archive',
  supplierContactManage: 'supplier.contact.manage',

  qualificationView: 'supplier.qualification.view',
  qualificationSubmit: 'supplier.qualification.submit',
  qualificationDecide: 'supplier.qualification.decide',

  supplierUserView: 'supplier.portal_user.view',
  supplierUserManage: 'supplier.portal_user.manage',

  agreementView: 'agreement.framework.view',
  agreementCreate: 'agreement.framework.create',
  agreementUpdate: 'agreement.framework.update',
  agreementActivate: 'agreement.framework.activate',
  agreementSuspend: 'agreement.framework.suspend',
  agreementTerminate: 'agreement.framework.terminate',
  agreementItemManage: 'agreement.item.manage',

  catalogueBrowse: 'catalogue.item.browse',
  catalogueView: 'catalogue.item.view',
  catalogueCreate: 'catalogue.item.create',
  catalogueUpdate: 'catalogue.item.update',
  catalogueArchive: 'catalogue.item.archive',
  catalogueSourceManage: 'catalogue.source.manage',
  catalogueBranchAccessManage: 'catalogue.branch_access.manage',
  catalogueImport: 'catalogue.item.import',

  // ── Epic 3 ─────────────────────────────────────────────────────────────
  budgetView: 'budget.budget.view',
  budgetCreate: 'budget.budget.create',
  budgetUpdate: 'budget.budget.update',
  budgetAllocate: 'budget.budget.allocate',
  budgetLedgerView: 'budget.ledger.view',
  budgetPeriodManage: 'budget.period.manage',

  approvalRuleView: 'approval.rule.view',
  approvalRuleCreate: 'approval.rule.create',
  approvalRuleUpdate: 'approval.rule.update',
  approvalRuleDelete: 'approval.rule.delete',
  approvalRuleSimulate: 'approval.rule.simulate',

  approvalWorkflowViewOwn: 'approval.workflow.view_own',
  approvalWorkflowViewAny: 'approval.workflow.view_any',
  approvalDecide: 'approval.workflow.decide',

  // ── Epic 4 ─────────────────────────────────────────────────────────────
  requestCreate: 'request.request.create',
  requestViewOwn: 'request.request.view_own',
  requestViewBranch: 'request.request.view_branch',
  requestViewAny: 'request.request.view_any',
  requestSubmit: 'request.request.submit',
  requestCancel: 'request.request.cancel',
  requestOffCatalogue: 'request.request.off_catalogue',
  requestProcurementReview: 'request.request.procurement_review',

  // ── Epic 5 ─────────────────────────────────────────────────────────────
  orderViewBranch: 'order.purchase_order.view_branch',
  orderViewAny: 'order.purchase_order.view_any',

  // RCV-03…RCV-06. Recording and closing short are deliberately separate:
  // R-033 FLW-22 assigns them to different roles.
  receiptView: 'receiving.receipt.view',
  receiptRecord: 'receiving.receipt.record',
  receiptCloseShort: 'receiving.receipt.close_short',

  // INV-01 / INV-02. Capture is finance's half of FLW-23; the supplier's own
  // door needs no tenant permission.
  invoiceView: 'invoice.supplier_invoice.view',
  invoiceCapture: 'invoice.supplier_invoice.capture',

  // SRC-01…SRC-12. Awarding is separate from running an enquiry.
  rfqView: 'sourcing.rfq.view',
  rfqManage: 'sourcing.rfq.manage',
  rfqAward: 'sourcing.rfq.award',
  orderGenerate: 'order.purchase_order.generate',
  // Separate from generating on purpose: preparing a draft costs nothing,
  // issuing commits the organisation and converts reserved budget.
  orderIssue: 'order.purchase_order.issue',
  orderSend: 'order.purchase_order.send',
  orderAmend: 'order.purchase_order.amend',
  orderCancel: 'order.purchase_order.cancel',
  orderDownload: 'order.purchase_order.download',
  notificationView: 'platform.notification.view',

  // RPT-01…RPT-08 and RPT-11. One permission opens the aggregates; carrying a
  // copy out of the product is a second, separate decision.
  reportView: 'report.report.view',
  reportExport: 'report.report.export',

  auditView: 'audit.log.view',
  auditExport: 'audit.log.export',

  onboardingView: 'onboarding.checklist.view',
  onboardingUpdate: 'onboarding.checklist.update',
  importRun: 'onboarding.import.run',
  importView: 'onboarding.import.view',
} as const;

export type PermissionSlug = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
